// A square scene's doors and walls on the lines between cells, twin of
// MapEdges (docs/architecture/maps.md, "Library and terrain"): `n[row][col]` is
// the edge cell (col, row) shares with (col, row - 1), `w[row][col]` the one it
// shares with (col - 1, row), `d[row][col]` the diagonal of a cut cell (cuts.ts).
// Hex scenes have none.
import type { LiveScene } from "../types/generated/live";
import { type CutLetter, cutAt } from "./cuts";
import { EMPTY, paintable, paintCells } from "./terrain";

// A side of a cell, and where a mark is kept: the sides' lines or a cut cell's diagonal.
export type EdgeKey = "n" | "w";
export type MarkKey = EdgeKey | "d";
export type Edge = { key: EdgeKey; col: number; row: number };
// A diagonal's `letter` is its cell's cut, which tells which way it runs; a mark read from the lists has none.
export type Mark = Edge | { key: "d"; col: number; row: number; letter?: CutLetter };
export type Edges = Partial<Record<MarkKey, string[]>>;
export type EdgeMode = "paint" | "remove";
type Point = { x: number; y: number };
type Grid = { kind: string; cell_w: number; cell_h: number; offset_x: number; offset_y: number };
type GridMath = { grid: Grid; centerOf: (col: number, row: number) => Point };
type Scene = Pick<LiveScene, "width_px" | "height_px">;

// What the table says of a code (twin of MapEdges::MARKS); "." is none, and no mark. A `fake` door is a wall the
// master sees as a door.
export type EdgeMark = { kind: "door" | "window" | "slit" | "grate" | "wall"; double?: true; locked?: true; fake?: true; secret?: true };
export type Switch = "double" | "locked" | "fake" | "secret";
export const MARKS: Record<string, EdgeMark> = {
	p: { kind: "door" },
	t: { kind: "door", locked: true },
	f: { kind: "door", fake: true },
	s: { kind: "door", secret: true },
	k: { kind: "door", secret: true, locked: true },
	d: { kind: "door", double: true },
	T: { kind: "door", double: true, locked: true },
	F: { kind: "door", double: true, fake: true },
	S: { kind: "door", double: true, secret: true },
	K: { kind: "door", double: true, secret: true, locked: true },
	j: { kind: "window" },
	J: { kind: "window", secret: true },
	e: { kind: "slit" },
	E: { kind: "slit", secret: true },
	g: { kind: "grate" },
	m: { kind: "wall" },
};
export const EDGE_CODES = [EMPTY, ...Object.keys(MARKS)];
export const markOf = (code: string): EdgeMark | undefined => (Object.hasOwn(MARKS, code) ? MARKS[code] : undefined);

// What players receive of a code (twin of MapEdges.players_code): a code the table lacks is a wall, never a gap.
export function playersCode(code: string) {
	if (code === EMPTY) return code;
	const mark = markOf(code);
	if (!mark || mark.secret) return "m";
	if (mark.kind === "door") return mark.double ? "d" : "p";
	return code;
}
const KEYS: MarkKey[] = ["n", "w", "d"];
// Of a cell: a drag samples its path this often, takes a line it passes this
// close to, and nothing this close to a grid corner along it.
const DRAG_STEP = 1 / 8;
const DRAG_REACH = 1 / 3;
const CORNER = 1 / 8;
// Of the shorter cell side: a tap this close to a door, window or arrow slit opens its menu.
const MENU_REACH = 0.3;

const isRows = (rows: unknown): rows is string[] => Array.isArray(rows) && rows.every((line) => typeof line === "string");

function isEdges(edges: unknown): edges is Edges {
	if (!edges || typeof edges !== "object" || Array.isArray(edges)) return false;
	return Object.entries(edges).every(([key, rows]) => (KEYS as string[]).includes(key) && isRows(rows));
}

function trim(rows: string[]) {
	const trimmed = rows.map((line) => line.replace(/\.+$/, ""));
	while (trimmed.length && trimmed.at(-1) === "") trimmed.pop();
	return trimmed;
}

// Spelled as terrain and fog are, so one set of edges has one spelling; a value
// in another shape is left as it is.
export function normalizeEdges<T>(edges: T): T | Edges {
	if (!isEdges(edges)) return edges;
	const normalized: Edges = {};
	for (const key of KEYS) {
		const rows = edges[key] ? trim(edges[key]) : [];
		if (rows.length) normalized[key] = rows;
	}
	return normalized;
}

export function sameEdges(a: Edges, b: Edges) {
	const [left, right] = [normalizeEdges(a) as Edges, normalizeEdges(b) as Edges];
	return KEYS.every((key) => (left[key] ?? []).join("\n") === (right[key] ?? []).join("\n"));
}

export function edgesOf(edges: unknown): Edges {
	return isEdges(edges) ? edges : {};
}

// What a scene write sends: `n` and `w` always, since a write replaces every list, and `d` only when a diagonal holds a mark.
export function edgesWrite(edges: Edges) {
	return { n: edges.n ?? [], w: edges.w ?? [], ...(edges.d?.length ? { d: edges.d } : {}) };
}

export function edgeAt(edges: Edges | null | undefined, { key, col, row }: Mark): string {
	const code = edges?.[key]?.[row]?.[col];
	return code || EMPTY;
}

// Every listed edge set to `code`, padding and trimming the rows.
export function setEdges(edges: Edges, list: Mark[], code: string): Edges {
	const next: Edges = { ...edges };
	for (const key of KEYS) {
		const cells = list.filter((edge) => edge.key === key);
		if (cells.length) next[key] = paintCells(edges[key] ?? [], cells, code);
	}
	return normalizeEdges(next) as Edges;
}

function neighbour({ key, col, row }: Edge) {
	return key === "n" ? { col, row: row - 1 } : { col: col - 1, row };
}

// Both of its cells are paintable, so the scene's outer boundary never is; a
// diagonal lies inside one cell (twin of MapEdges.paintable?).
export function edgePaintable(math: GridMath, scene: Scene, edge: Mark): boolean {
	if (math.grid.kind !== "square") return false;
	return paintable(math, scene, { col: edge.col, row: edge.row }) && (edge.key === "d" || paintable(math, scene, neighbour(edge)));
}

// Cuts `a` and `c` share the diagonal from north-west to south-east, `b` and `d` the other one.
const slopesDown = (letter?: CutLetter) => letter !== "b" && letter !== "d";

// The mark's ends in image pixels.
export function edgeSegment(math: GridMath, mark: Mark) {
	const { cell_w, cell_h } = math.grid;
	const centre = math.centerOf(mark.col, mark.row);
	const x1 = centre.x - cell_w / 2;
	const y1 = centre.y - cell_h / 2;
	if (mark.key === "d") return slopesDown(mark.letter) ? { x1, y1, x2: x1 + cell_w, y2: y1 + cell_h } : { x1: x1 + cell_w, y1, x2: x1, y2: y1 + cell_h };
	return mark.key === "n" ? { x1, y1, x2: x1 + cell_w, y2: y1 } : { x1, y1, x2: x1, y2: y1 + cell_h };
}

function gridUnits(grid: Grid, { x, y }: Point) {
	return { u: (x - grid.offset_x) / grid.cell_w, v: (y - grid.offset_y) / grid.cell_h };
}

// How far the point, in the cell of fractions (fu, fv) of its sides, is from the diagonal of a cell cut `letter`, in pixels.
function diagonalDistance({ cell_w, cell_h }: Grid, letter: CutLetter, fu: number, fv: number) {
	return (Math.abs(slopesDown(letter) ? fu - fv : fu + fv - 1) * cell_w * cell_h) / Math.hypot(cell_w, cell_h);
}

// The tapped cell's side nearest the point, or its diagonal when the cell is cut (`cuts`) and the point is nearer to
// that than to any side; its south and east sides are its neighbours' north and west.
export function nearestSide(math: GridMath, point: Point, cuts?: readonly string[]): Mark | null {
	const { grid } = math;
	if (grid.kind !== "square") return null;
	const { u, v } = gridUnits(grid, point);
	const col = Math.floor(u);
	const row = Math.floor(v);
	const sides: Array<[number, Mark]> = [
		[(v - row) * grid.cell_h, { key: "n", col, row }],
		[(row + 1 - v) * grid.cell_h, { key: "n", col, row: row + 1 }],
		[(u - col) * grid.cell_w, { key: "w", col, row }],
		[(col + 1 - u) * grid.cell_w, { key: "w", col: col + 1, row }],
	];
	const letter = cutAt(cuts, col, row);
	if (letter) sides.push([diagonalDistance(grid, letter, u - col, v - row), { key: "d", col, row, letter }]);
	return sides.reduce((best, side) => (side[0] < best[0] ? side : best))[1];
}

// The grid-line segment one sample takes: the nearer line within reach,
// unless the sample is near a grid corner along it. In a cut cell the diagonal
// is a line of its own, taken in the same way when it is the nearest.
function sampleEdge(grid: Grid, point: Point, cuts?: readonly string[]): Mark | null {
	const { u, v } = gridUnits(grid, point);
	const [col0, row0] = [Math.floor(u), Math.floor(v)];
	const letter = cutAt(cuts, col0, row0);
	if (letter) {
		const [fu, fv] = [u - col0, v - row0];
		const sideDistance = Math.min(fu * grid.cell_w, (1 - fu) * grid.cell_w, fv * grid.cell_h, (1 - fv) * grid.cell_h);
		const across = diagonalDistance(grid, letter, fu, fv);
		// Measured along the diagonal's own direction: from the north-west end for `a` and `c`, from the north-east one for `b` and `d`.
		const along = (slopesDown(letter) ? fu + fv : fu + 1 - fv) / 2;
		if (across < sideDistance && across <= Math.min(grid.cell_w, grid.cell_h) * DRAG_REACH && Math.abs(along - Math.round(along)) >= CORNER) {
			return { key: "d", col: col0, row: row0, letter };
		}
	}
	const col = Math.round(u);
	const row = Math.round(v);
	const across = Math.abs(u - col) * grid.cell_w;
	const down = Math.abs(v - row) * grid.cell_h;
	const vertical = across <= down;
	if (vertical ? across > grid.cell_w * DRAG_REACH : down > grid.cell_h * DRAG_REACH) return null;
	const along = vertical ? v : u;
	if (Math.abs(along - Math.round(along)) < CORNER) return null;
	return vertical ? { key: "w", col, row: Math.floor(v) } : { key: "n", col: Math.floor(u), row };
}

// Every grid-line segment a drag from one point to another runs along, in
// order and once each.
export function dragEdges(math: GridMath, from: Point, to: Point, cuts?: readonly string[]): Mark[] {
	const { grid } = math;
	if (grid.kind !== "square") return [];
	const step = Math.min(grid.cell_w, grid.cell_h) * DRAG_STEP;
	const count = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / step));
	const edges: Mark[] = [];
	const seen = new Set<string>();
	for (let index = 0; index <= count; index++) {
		const t = index / count;
		const edge = sampleEdge(grid, { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t }, cuts);
		if (!edge) continue;
		const id = edgeId(edge);
		if (seen.has(id)) continue;
		seen.add(id);
		edges.push(edge);
	}
	return edges;
}

export function edgeId({ key, col, row }: Mark) {
	return `${key}${col},${row}`;
}

export const kindOf = (code: string) => markOf(code)?.kind;

// The first edge decides: holding a mark of the brush's kind, the gesture
// removes that kind; otherwise it paints. The eraser (".") always removes.
export function gestureMode(edges: Edges, first: Mark, brush: string): EdgeMode {
	return brush === EMPTY || kindOf(edgeAt(edges, first)) === kindOf(brush) ? "remove" : "paint";
}

// Painting writes the brush's plain code but leaves an edge that already holds
// its kind alone, so a drag never resets a door's switches; removing takes
// only the brush's kind, or every kind for the eraser.
export function applyEdgeGesture(edges: Edges, taken: Mark[], brush: string, mode: EdgeMode): Edges {
	const kind = kindOf(brush);
	if (mode === "paint")
		return setEdges(
			edges,
			taken.filter((edge) => kindOf(edgeAt(edges, edge)) !== kind),
			brush,
		);
	return setEdges(edges, brush === EMPTY ? taken : taken.filter((edge) => kindOf(edgeAt(edges, edge)) === kind), EMPTY);
}

// Whether a mark's kind has a menu: a door, a window or an arrow slit.
export const hasMenu = (code: string) => ["door", "window", "slit"].includes(kindOf(code) ?? "");

// The door, window or arrow slit a tap lands on: the tapped cell's nearest
// side or diagonal, when it holds one that is drawn and the tap is within reach of it.
export function menuMarkAt(math: GridMath, scene: Scene, edges: Edges, point: Point, cuts?: readonly string[]): Mark | null {
	const edge = nearestSide(math, point, cuts);
	if (!edge || !hasMenu(edgeAt(edges, edge)) || !edgePaintable(math, scene, edge)) return null;
	const { x1, y1, x2, y2 } = edgeSegment(math, edge);
	let distance = edge.key === "n" ? Math.abs(point.y - y1) : Math.abs(point.x - x1);
	if (edge.key === "d") distance = Math.abs((x2 - x1) * (y1 - point.y) - (x1 - point.x) * (y2 - y1)) / Math.hypot(x2 - x1, y2 - y1);
	return distance <= Math.min(math.grid.cell_w, math.grid.cell_h) * MENU_REACH ? edge : null;
}

const SWITCHES: Switch[] = ["double", "locked", "fake", "secret"];

// The code of the same kind with one switch set, or null where the kind has no such switch or the table refuses the
// state: turning Falsa on turns Trancada and Secreta off, Secreta on turns Falsa off, and Trancada cannot go on beside Falsa.
export function withSwitch(code: string, name: Switch, on: boolean): string | null {
	const mark = markOf(code);
	if (!mark || !hasMenu(code) || (mark.kind !== "door" && name !== "secret")) return null;
	if (on && name === "locked" && mark.fake) return null;
	const next = new Set(SWITCHES.filter((each) => mark[each]));
	if (on) next.add(name);
	else next.delete(name);
	if (on && name === "fake") {
		next.delete("locked");
		next.delete("secret");
	}
	if (on && name === "secret") next.delete("fake");
	const same = ([, other]: [string, EdgeMark]) => other.kind === mark.kind && SWITCHES.every((each) => Boolean(other[each]) === next.has(each));
	return Object.entries(MARKS).find(same)?.[0] ?? null;
}
